import { css } from "@emotion/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpcClient } from "../lib/trpc";
import { Button } from "../components/button";
import { Modal } from "../components/modal";
import {
  appContainer,
  appContainerFull,
  appHeader,
  appTitle,
  appSubtitle,
  loading as loadingStyle,
  error as errorStyle,
  emptyState,
  formGroup,
  formLabel,
  formInput,
  modalActions,
} from "@/styles/shared";
import { CaretUp, CaretDown, MagnifyingGlass, UserPlus, PencilSimple } from "@phosphor-icons/react";

const PAGE_SIZE = 25;

type SortField = "firstname" | "lastname" | "phone";
type SortDirection = "asc" | "desc";

type Player = {
  id: string;
  firstname: string;
  lastname: string;
  phone: string;
};

const tableContainer = css`
  overflow-x: auto;
  margin-top: 24px;
`;

const table = css`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
`;

const tableHeader = css`
  background: var(--color-bg-warm);
  border-bottom: 2px solid var(--color-border);
`;

const tableHeaderCell = css`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: background 0.2s ease;

  &:hover {
    background: var(--dropdown-hover);
  }
`;

const tableHeaderCellContent = css`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const sortIcon = css`
  opacity: 0.3;
  transition: opacity 0.2s ease;
`;

const sortIconActive = css`
  opacity: 1;
  color: var(--color-primary);
`;

const tableRow = css`
  border-bottom: 1px solid var(--color-border);
  transition: background 0.2s ease;

  &:hover {
    background: var(--dropdown-hover);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const tableCell = css`
  padding: 12px 16px;
  color: var(--text-secondary);
`;

const searchContainer = css`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const searchInputWrapper = css`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;
`;

const searchInput = css`
  width: 100%;
  padding: 10px 16px 10px 40px;
  border: 2px solid var(--input-border);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  color: var(--text-primary);
  background: var(--input-bg);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--input-border-focus);
  }

  &::placeholder {
    color: var(--text-accent);
  }
`;

const searchIcon = css`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-accent);
  pointer-events: none;
`;

const paginationContainer = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const paginationInfo = css`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const paginationButtons = css`
  display: flex;
  gap: 8px;
`;

const phoneCell = css`
  font-family: monospace;
  letter-spacing: 0.5px;
`;

const headerActions = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const formError = css`
  color: var(--color-error, #dc2626);
  font-size: 0.85rem;
  margin-top: 4px;
`;

const actionsCell = css`
  width: 80px;
  text-align: center;
`;

const editButton = css`
  padding: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--text-secondary);
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;

  &:hover {
    background: var(--dropdown-hover);
    color: var(--color-primary);
  }
`;

const tableHeaderCellStatic = css`
  padding: 12px 16px;
  text-align: center;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
`;

const importDescription = css`
  color: var(--text-secondary);
  margin-bottom: 8px;
`;

const importFormatHint = css`
  font-size: var(--text-sm);
  color: var(--text-muted);
  font-style: italic;
  margin-bottom: 16px;
`;

const fileInput = css`
  width: 100%;
  padding: 12px;
  border: 2px dashed var(--input-border);
  border-radius: var(--radius-md);
  background: var(--input-bg);
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--input-border-focus);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const importingText = css`
  color: var(--text-secondary);
  font-style: italic;
  margin-top: 12px;
`;

const importErrorBox = css`
  margin-top: 12px;
  padding: 12px;
  background: var(--error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  max-height: 200px;
  overflow-y: auto;
`;

const importErrorText = css`
  color: var(--error-text);
  font-size: var(--text-sm);
  white-space: pre-wrap;
  margin: 0;
  font-family: inherit;
`;

const importSuccessText = css`
  color: var(--color-success, #10b981);
  font-weight: 600;
  margin-top: 12px;
`;

function formatPhone(phone: string): string {
  // Format: 0612345678 → 06 12 34 56 78
  if (phone.length === 10 && phone.startsWith("0")) {
    return phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
  }
  // Format: +33612345678 → +33 6 12 34 56 78
  if (phone.startsWith("+33") && phone.length === 12) {
    const number = phone.slice(3);
    return `+33 ${number[0]} ${number.slice(1, 3)} ${number.slice(3, 5)} ${number.slice(5, 7)} ${number.slice(7)}`;
  }
  return phone;
}

const PHONE_REGEX = /^(?:0[1-9]\d{8}|\+33[1-9]\d{8})$/;

export function PlayersPage() {
  const { t } = useTranslation();
  const [players, setPlayers] = useState<Player[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<SortField>("lastname");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  // Add/Edit player modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [formData, setFormData] = useState({ firstname: "", lastname: "", phone: "" });
  const [formErrors, setFormErrors] = useState<{ firstname?: string; lastname?: string; phone?: string }>({});

  // CSV import state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<number | null>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadPlayers = useCallback(async () => {
    setIsLoading(true);
    setErr(null);
    try {
      const result = await trpcClient.listPlayers.query({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sortBy,
        sortDirection,
        search: searchDebounced || undefined,
      });
      setPlayers(result.players);
      setTotal(result.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load players");
    } finally {
      setIsLoading(false);
    }
  }, [page, sortBy, sortDirection, searchDebounced]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDirection("asc");
    }
    setPage(0);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const startItem = page * PAGE_SIZE + 1;
  const endItem = Math.min((page + 1) * PAGE_SIZE, total);

  const renderSortIcon = (field: SortField) => {
    const isActive = sortBy === field;
    const Icon = isActive && sortDirection === "desc" ? CaretDown : CaretUp;
    return <Icon size={14} css={[sortIcon, isActive && sortIconActive]} weight="bold" />;
  };

  const openAddModal = () => {
    setEditingPlayer(null);
    setFormData({ firstname: "", lastname: "", phone: "" });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (player: Player) => {
    setEditingPlayer(player);
    setFormData({ firstname: player.firstname, lastname: player.lastname, phone: player.phone });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingPlayer(null);
    }
  };

  const validateForm = () => {
    const errors: typeof formErrors = {};
    if (!formData.firstname.trim()) {
      errors.firstname = t("players.errors.firstnameRequired");
    }
    if (!formData.lastname.trim()) {
      errors.lastname = t("players.errors.lastnameRequired");
    }
    const cleanPhone = formData.phone.replace(/[\s.\-]/g, "");
    if (!cleanPhone) {
      errors.phone = t("players.errors.phoneRequired");
    } else if (!PHONE_REGEX.test(cleanPhone)) {
      errors.phone = t("players.errors.phoneInvalid");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePlayer = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (editingPlayer) {
        await trpcClient.updatePlayer.mutate({
          id: editingPlayer.id,
          firstname: formData.firstname.trim(),
          lastname: formData.lastname.trim(),
          phone: formData.phone,
        });
      } else {
        await trpcClient.createPlayer.mutate({
          firstname: formData.firstname.trim(),
          lastname: formData.lastname.trim(),
          phone: formData.phone,
        });
      }
      setIsModalOpen(false);
      setEditingPlayer(null);
      loadPlayers();
    } catch (e) {
      setFormErrors({ phone: e instanceof Error ? e.message : t("players.errors.saveFailed") });
    } finally {
      setIsSaving(false);
    }
  };

  const openImportModal = () => {
    setImportError(null);
    setImportSuccess(null);
    setIsImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (!isImporting) {
      setIsImportModalOpen(false);
      setImportError(null);
      setImportSuccess(null);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };

  const parseCSV = (text: string): string[][] => {
    const lines = text.split(/\r?\n/).filter(line => line.trim() !== "");
    return lines.map(line => {
      // Simple CSV parsing (handles basic cases)
      const result: string[] = [];
      let current = "";
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if ((char === "," || char === ";") && !inQuotes) {
          result.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    });
  };

  const handleCSVImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportError(null);
    setImportSuccess(null);

    try {
      const text = await file.text();
      const rows = parseCSV(text);

      if (rows.length === 0) {
        setImportError(t("players.import.emptyFile"));
        setIsImporting(false);
        return;
      }

      // Check if first row is a header (skip if it contains "prenom", "nom", "tel")
      let dataRows = rows;
      const firstRow = rows[0]?.map(cell => cell.toLowerCase()) ?? [];
      if (firstRow.some(cell => ["prenom", "prénom", "nom", "tel", "telephone", "téléphone", "firstname", "lastname", "phone"].includes(cell))) {
        dataRows = rows.slice(1);
      }

      if (dataRows.length === 0) {
        setImportError(t("players.import.noData"));
        setIsImporting(false);
        return;
      }

      // Validate all rows first
      const errors: string[] = [];
      const validatedPlayers: { firstname: string; lastname: string; phone: string }[] = [];

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const lineNum = i + 1 + (rows.length - dataRows.length); // Adjust for header
        
        if (!row || row.length < 3) {
          errors.push(t("players.import.rowIncomplete", { line: lineNum }));
          continue;
        }

        const [firstname, lastname, phone] = row;

        if (!firstname?.trim()) {
          errors.push(t("players.import.missingFirstname", { line: lineNum }));
          continue;
        }

        if (!lastname?.trim()) {
          errors.push(t("players.import.missingLastname", { line: lineNum }));
          continue;
        }

        if (!phone?.trim()) {
          errors.push(t("players.import.missingPhone", { line: lineNum }));
          continue;
        }

        const cleanPhone = phone.replace(/[\s.\-]/g, "");
        if (!PHONE_REGEX.test(cleanPhone)) {
          errors.push(t("players.import.invalidPhone", { line: lineNum, phone }));
          continue;
        }

        validatedPlayers.push({
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          phone: cleanPhone,
        });
      }

      // If any validation errors, abort import
      if (errors.length > 0) {
        setImportError(errors.join("\n"));
        setIsImporting(false);
        if (csvInputRef.current) csvInputRef.current.value = "";
        return;
      }

      // Import all validated players
      for (const player of validatedPlayers) {
        await trpcClient.createPlayer.mutate(player);
      }

      setImportSuccess(validatedPlayers.length);
      loadPlayers();
      if (csvInputRef.current) csvInputRef.current.value = "";
    } catch (e) {
      setImportError(e instanceof Error ? e.message : t("players.import.failed"));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div css={[appContainer, appContainerFull]}>
      <header css={appHeader}>
        <div>
          <h1 css={appTitle}>{t("players.title")}</h1>
          <p css={appSubtitle}>{t("players.subtitle", { count: total })}</p>
        </div>
        <div css={headerActions}>
          <Button variant="secondary" onClick={openImportModal}>
            {t("players.importCSV")}
          </Button>
          <Button variant="primary" onClick={openAddModal}>
            <UserPlus size={18} weight="bold" />
            {t("players.addPlayer")}
          </Button>
        </div>
      </header>

      <div css={searchContainer}>
        <div css={searchInputWrapper}>
          <MagnifyingGlass size={18} css={searchIcon} />
          <input
            type="text"
            css={searchInput}
            placeholder={t("players.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {err && <div css={errorStyle}>{err}</div>}

      {isLoading && players.length === 0 ? (
        <div css={loadingStyle}>{t("common.loading")}</div>
      ) : players.length === 0 ? (
        <div css={emptyState}>
          <p>{searchDebounced ? t("players.noResults") : t("players.empty")}</p>
        </div>
      ) : (
        <>
          <div css={tableContainer}>
            <table css={table}>
              <thead css={tableHeader}>
                <tr>
                  <th css={tableHeaderCell} onClick={() => handleSort("lastname")}>
                    <div css={tableHeaderCellContent}>
                      {t("players.lastname")}
                      {renderSortIcon("lastname")}
                    </div>
                  </th>
                  <th css={tableHeaderCell} onClick={() => handleSort("firstname")}>
                    <div css={tableHeaderCellContent}>
                      {t("players.firstname")}
                      {renderSortIcon("firstname")}
                    </div>
                  </th>
                  <th css={tableHeaderCell} onClick={() => handleSort("phone")}>
                    <div css={tableHeaderCellContent}>
                      {t("players.phone")}
                      {renderSortIcon("phone")}
                    </div>
                  </th>
                  <th css={tableHeaderCellStatic}>{t("players.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {players.map((player) => (
                  <tr key={player.id} css={tableRow}>
                    <td css={tableCell}>{player.lastname}</td>
                    <td css={tableCell}>{player.firstname}</td>
                    <td css={[tableCell, phoneCell]}>{formatPhone(player.phone)}</td>
                    <td css={[tableCell, actionsCell]}>
                      <button
                        css={editButton}
                        onClick={() => openEditModal(player)}
                        title={t("players.editPlayer")}
                      >
                        <PencilSimple size={18} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div css={paginationContainer}>
            <span css={paginationInfo}>
              {t("players.pagination", { start: startItem, end: endItem, total })}
            </span>
            <div css={paginationButtons}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p - 1)}
                disabled={page === 0}
              >
                {t("common.previous")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1}
              >
                {t("common.next")}
              </Button>
            </div>
          </div>
        </>
      )}

      <Modal
        open={isModalOpen}
        onClose={closeModal}
        title={editingPlayer ? t("players.editPlayerTitle") : t("players.addPlayerTitle")}
      >
        <div css={formGroup}>
          <label css={formLabel}>{t("players.firstname")} *</label>
          <input
            type="text"
            css={formInput}
            value={formData.firstname}
            onChange={(e) => setFormData((p) => ({ ...p, firstname: e.target.value }))}
            placeholder={t("players.firstnamePlaceholder")}
            disabled={isSaving}
          />
          {formErrors.firstname && <span css={formError}>{formErrors.firstname}</span>}
        </div>

        <div css={formGroup}>
          <label css={formLabel}>{t("players.lastname")} *</label>
          <input
            type="text"
            css={formInput}
            value={formData.lastname}
            onChange={(e) => setFormData((p) => ({ ...p, lastname: e.target.value }))}
            placeholder={t("players.lastnamePlaceholder")}
            disabled={isSaving}
          />
          {formErrors.lastname && <span css={formError}>{formErrors.lastname}</span>}
        </div>

        <div css={formGroup}>
          <label css={formLabel}>{t("players.phone")} *</label>
          <input
            type="tel"
            css={formInput}
            value={formData.phone}
            onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
            placeholder={t("players.phonePlaceholder")}
            disabled={isSaving}
          />
          {formErrors.phone && <span css={formError}>{formErrors.phone}</span>}
        </div>

        <div css={modalActions}>
          <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={handleSavePlayer} disabled={isSaving}>
            {isSaving ? t("players.saving") : t("common.save")}
          </Button>
        </div>
      </Modal>

      {/* CSV Import Modal */}
      <Modal
        open={isImportModalOpen}
        onClose={closeImportModal}
        title={t("players.import.title")}
      >
        <p css={importDescription}>{t("players.import.description")}</p>
        <p css={importFormatHint}>{t("players.import.formatHint")}</p>

        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleCSVImport}
          disabled={isImporting}
          css={fileInput}
        />

        {isImporting && (
          <p css={importingText}>{t("players.import.importing")}</p>
        )}

        {importError && (
          <div css={importErrorBox}>
            <pre css={importErrorText}>{importError}</pre>
          </div>
        )}

        {importSuccess !== null && (
          <p css={importSuccessText}>
            {t("players.import.success", { count: importSuccess })}
          </p>
        )}

        <div css={modalActions}>
          <Button variant="secondary" onClick={closeImportModal} disabled={isImporting}>
            {t("common.close")}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
